import { useState, useEffect } from 'react';
import api from '../api';

const BOOKING_TOKENS_KEY = 'booking_tokens';
const BOOKING_TOKEN_LEGACY_KEY = 'booking_token';
const BOOKING_TOKEN_MAX_AGE = 365 * 24 * 60 * 60;

function getStoredBookingTokens() {
  try {
    const raw = localStorage.getItem(BOOKING_TOKENS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    }
    const legacy = localStorage.getItem(BOOKING_TOKEN_LEGACY_KEY);
    if (legacy) return [legacy];
    return [];
  } catch {
    return [];
  }
}

function setStoredBookingTokens(tokens) {
  try {
    const arr = [...new Set(tokens)].filter(Boolean);
    localStorage.setItem(BOOKING_TOKENS_KEY, JSON.stringify(arr));
    if (arr.length > 0) {
      document.cookie = `booking_token=${encodeURIComponent(arr[0])}; path=/; max-age=${BOOKING_TOKEN_MAX_AGE}; SameSite=Lax`;
    } else {
      document.cookie = 'booking_token=; path=/; max-age=0';
    }
  } catch (_) {}
}

function addStoredBookingToken(token) {
  const tokens = getStoredBookingTokens();
  if (tokens.includes(token)) return;
  setStoredBookingTokens([...tokens, token]);
}

function BookingNotesEditor({ initialMessage, onSave, saving, saved }) {
  const [message, setMessage] = useState(initialMessage);
  useEffect(() => { setMessage(initialMessage); }, [initialMessage]);
  return (
    <>
      <textarea
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={3}
        style={{
          width: '100%',
          padding: '13px 16px',
          background: 'var(--background)',
          color: 'var(--text)',
          border: '1px solid var(--border-strong, rgba(255,255,255,0.12))',
          borderRadius: '6px',
          fontFamily: 'inherit',
          fontSize: '0.92rem',
          lineHeight: '1.6',
          transition: 'border-color 0.3s ease, box-shadow 0.3s ease'
        }}
        onFocus={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.boxShadow = '0 0 0 3px rgba(201,160,80,0.12)'; }}
        onBlur={e => { e.target.style.borderColor = ''; e.target.style.boxShadow = ''; }}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px' }}>
        <button type="button" className="btn btn-small btn-primary" onClick={() => onSave(message)} disabled={saving}>
          {saving ? 'A guardar...' : 'Guardar notas'}
        </button>
        {saved && <span style={{ color: 'var(--accent)', fontSize: '0.85rem', fontWeight: 500 }}>Notas guardadas.</span>}
      </div>
    </>
  );
}

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    requested_date: '',
    requested_time: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [bookingList, setBookingList] = useState([]);
  const [bookingStatusLoading, setBookingStatusLoading] = useState(true);
  const [savingToken, setSavingToken] = useState(null);
  const [savedToken, setSavedToken] = useState(null);
  const [showNewBookingForm, setShowNewBookingForm] = useState(false);

  useEffect(() => {
    const tokens = getStoredBookingTokens();
    if (tokens.length === 0) {
      setBookingStatusLoading(false);
      return;
    }
    Promise.all(
      tokens.map(async (token) => {
        try {
          const res = await api.get(`/api/bookings/status/${token}`);
          return { token, data: res.data, is404: false };
        } catch (err) {
          const status = err.response?.status;
          return { token, data: null, is404: status === 404 };
        }
      })
    ).then(results => {
      const valid = results.filter(r => r && r.data).map(r => ({ token: r.token, ...r.data }));
      const keptTokens = tokens.filter((_, i) => {
        const r = results[i];
        if (!r) return true;
        if (r.data) return true;
        return !r.is404;
      });
      setBookingList(valid);
      setStoredBookingTokens(keptTokens);
    }).finally(() => setBookingStatusLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const { data } = await api.post('/api/bookings', formData);
      if (data?.token) {
        addStoredBookingToken(data.token);
        setBookingList(prev => [...prev, { token: data.token, status: data?.status || 'pending', message: formData.message, requested_date: data?.requested_date, requested_time: data?.requested_time, confirmed_date: data?.confirmed_date, confirmed_time: data?.confirmed_time, created_at: data?.created_at }]);
      }
      setStatus({ type: 'success', message: 'O seu pedido de marcação foi enviado. Confirmaremos a sua visita e responderemos em breve.' });
      setFormData({ name: '', email: '', phone: '', requested_date: '', requested_time: '', message: '' });
      setShowNewBookingForm(false);
    } catch (err) {
      setStatus({ type: 'error', message: err.response?.data?.error || 'Não foi possível enviar o pedido. Por favor, tente novamente.' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveNotes = async (token, message) => {
    setSavingToken(token);
    setSavedToken(null);
    try {
      const { data } = await api.patch(`/api/bookings/status/${token}`, { message });
      setBookingList(prev => prev.map(b => b.token === token ? { ...b, message: data?.message ?? message } : b));
      setSavedToken(token);
      setTimeout(() => setSavedToken(null), 3000);
    } catch (_) {
      setStatus({ type: 'error', message: 'Não foi possível guardar as notas.' });
    } finally {
      setSavingToken(null);
    }
  };

  const formatDate = (d) => (!d ? '' : new Date(d).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short', year: 'numeric' }));
  const hasBookings = !bookingStatusLoading && bookingList.length > 0;

  return (
    <div className="container" style={{ paddingTop: '48px', paddingBottom: '72px', maxWidth: '640px' }}>
      <h1 style={{ marginBottom: '8px', fontSize: '2.2rem' }}>Marcar uma consulta</h1>
      <hr style={{ width: '50px', height: '2px', background: 'var(--accent)', border: 'none', marginBottom: '16px' }} />
      <p style={{ color: 'var(--text-secondary)', marginBottom: '32px', fontWeight: 300, lineHeight: '1.7' }}>
        Peça uma visita. Confirmaremos a sua marcação e sugeriremos uma data e hora.
      </p>

      {status.message && (
        <div className={`message ${status.type}`} style={{ marginBottom: '24px' }}>
          {status.message}
        </div>
      )}

      {hasBookings && bookingList.some(b => b.status === 'confirmed') && (() => {
        const confirmed = bookingList.find(b => b.status === 'confirmed' && (b.confirmed_date || b.confirmed_time));
        const cStr = confirmed ? [formatDate(confirmed.confirmed_date), confirmed.confirmed_time].filter(Boolean).join(' às ') : '';
        const rStr = confirmed ? [formatDate(confirmed.requested_date), confirmed.requested_time].filter(Boolean).join(' às ') : '';
        const changed = cStr && rStr && cStr !== rStr;
        return (
          <div style={{
            marginBottom: '28px',
            padding: '20px 24px',
            background: 'linear-gradient(135deg, rgba(46, 204, 113, 0.15), rgba(46, 204, 113, 0.05))',
            border: '1px solid rgba(46, 204, 113, 0.3)',
            borderRadius: 'var(--radius-lg, 14px)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.3rem', color: '#2ecc71' }}>✓</span>
              <div>
                <strong style={{ color: 'var(--text)', fontSize: '1.05rem' }}>Marcação confirmada</strong>
                <div style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', marginTop: '4px' }}>
                  {cStr ? `Data e hora: ${cStr}` : 'Data e hora a combinar.'}
                </div>
              </div>
            </div>
            {changed && (
              <div style={{
                marginTop: '12px',
                padding: '10px 14px',
                background: 'rgba(241, 196, 15, 0.1)',
                border: '1px solid rgba(241, 196, 15, 0.25)',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: 'var(--text-secondary)'
              }}>
                Nota: a data/hora foi alterada em relação ao seu pedido original ({rStr}).
              </div>
            )}
          </div>
        );
      })()}

      {hasBookings && (
        <section style={{ marginBottom: '40px' }}>
          <h2 style={{ fontSize: '1.15rem', marginBottom: '20px', fontWeight: 500 }}>As suas marcações</h2>
          {bookingList.map((booking) => {
            const isConfirmed = booking.status === 'confirmed';
            const isCancelled = booking.status === 'cancelled';
            const statusLabel = isConfirmed ? 'Confirmada' : isCancelled ? 'Cancelada' : 'A aguardar confirmação';
            const statusColor = isConfirmed ? '#2ecc71' : isCancelled ? '#e74c3c' : '#f39c12';

            const requestedStr = [formatDate(booking.requested_date), booking.requested_time].filter(Boolean).join(' às ');
            const confirmedStr = [formatDate(booking.confirmed_date), booking.confirmed_time].filter(Boolean).join(' às ');
            const dateChanged = confirmedStr && requestedStr && confirmedStr !== requestedStr;

            return (
              <div
                key={booking.token}
                style={{
                  marginBottom: '20px',
                  padding: '24px',
                  border: `1px solid ${statusColor}25`,
                  borderRadius: 'var(--radius-lg, 14px)',
                  background: 'var(--background-secondary)',
                  borderLeft: `3px solid ${statusColor}`
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: statusColor, flexShrink: 0 }} />
                  <strong style={{ fontSize: '0.95rem', letterSpacing: '0.01em' }}>{statusLabel}</strong>
                </div>

                <div style={{
                  marginBottom: '16px',
                  padding: '14px 18px',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '8px',
                  fontSize: '0.9rem'
                }}>
                  {requestedStr && (
                    <div style={{ color: 'var(--text-secondary)', marginBottom: confirmedStr ? '8px' : 0 }}>
                      <span style={{ fontWeight: 600, fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pedido:</span> {requestedStr}
                    </div>
                  )}
                  {confirmedStr && (
                    <div style={{ color: 'var(--text)', fontWeight: 500 }}>
                      {isConfirmed ? '✓ ' : ''}Data confirmada: {confirmedStr}
                    </div>
                  )}
                  {dateChanged && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      background: 'rgba(46, 204, 113, 0.1)',
                      border: '1px solid rgba(46, 204, 113, 0.2)',
                      borderRadius: '6px',
                      fontSize: '0.82rem',
                      color: 'var(--text-secondary)'
                    }}>
                      A data/hora foi alterada em relação ao pedido original.
                    </div>
                  )}
                  {!confirmedStr && !requestedStr && (
                    <div style={{ color: 'var(--text-secondary)' }}>Data a combinar.</div>
                  )}
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>Notas da marcação</label>
                  <BookingNotesEditor
                    initialMessage={booking.message || ''}
                    onSave={(msg) => handleSaveNotes(booking.token, msg)}
                    saving={savingToken === booking.token}
                    saved={savedToken === booking.token}
                  />
                </div>
              </div>
            );
          })}
        </section>
      )}

      <section>
        {hasBookings && !showNewBookingForm ? (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowNewBookingForm(true)}
          >
            + Pedir nova marcação
          </button>
        ) : (
          <>
            <h2 style={{ fontSize: '1.15rem', marginBottom: '20px', fontWeight: 500 }}>
              {hasBookings ? 'Pedir nova marcação' : 'Preencha o formulário'}
            </h2>
            {hasBookings && (
              <button
                type="button"
                className="btn btn-small btn-secondary"
                onClick={() => setShowNewBookingForm(false)}
                style={{ marginBottom: '20px' }}
              >
                Fechar formulário
              </button>
            )}

            <form onSubmit={handleSubmit} style={{
              padding: '32px',
              background: 'var(--background-secondary)',
              borderRadius: 'var(--radius-lg, 14px)',
              border: '1px solid var(--border, rgba(255,255,255,0.06))'
            }}>
              <div className="form-group">
                <label htmlFor="name">Nome *</label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="email">E-mail *</label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Telefone</label>
                <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div className="form-group">
                  <label htmlFor="requested_date">Data preferida</label>
                  <input type="date" id="requested_date" name="requested_date" value={formData.requested_date} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label htmlFor="requested_time">Hora preferida</label>
                  <input type="time" id="requested_time" name="requested_time" value={formData.requested_time} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">Mensagem / notas</label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} />
              </div>

              <button type="submit" className="btn btn-primary" style={{ width: '100%', padding: '14px' }} disabled={submitting}>
                {submitting ? 'A enviar...' : 'Pedir marcação'}
              </button>
            </form>
          </>
        )}
      </section>
    </div>
  );
}

export default Contact;
